// src/utils/subscriptionHelpers.js
//
// Sprint 2c-2a foundation: subscription lifecycle helpers.
// Migrated from prototype/between-the-lines-v2.html lines 6166-6187 (3 helpers).
//
// Sprint 2c-2b consumers: AccountPage, MyPurchasesPanel.
// Sprint 3 consumer (likely): admin status badge logic.
//
// IMPORTANT — Production boundary:
// Helpers compute client-side from state argument only. NO real entitlement
// enforcement here. canAccessPack() is a CLIENT-SIDE DISPLAY/ACCESS HELPER ONLY,
// NOT a production entitlement gate. Production entitlement enforcement happens
// in Cloud Functions per Phase 0 architecture Doc #1 (entitlement V2.1) and
// Doc #4 (security V1.1) — Phase 2/4 scope.
//
// getSubscription() synthetic fallback is prototype/localStorage compatibility
// only (preserves access for pre-subscription `purchased` entries). Phase 2/4
// Firestore migration MUST NOT persist synthetic subs to backend; Cloud Function
// entitlement checks must distinguish real subscription documents from
// synthetic client-side fallback inferences.

import { isoToday, addYearsIso, daysUntil } from './dateHelpers.js';
import { SUBSCRIPTION_WARN_DAYS } from '../config/subscriptions.js';

// Returns subscription object or null. Falls back to a synthetic active sub
// for legacy `purchased` entries with no sub record (so pre-existing data keeps access).
//
// PRODUCTION BOUNDARY: synthetic:true flag marks prototype-only fallback. Phase 2/4
// backend entitlement MUST NOT rely on synthetic records.
//
// Migrated verbatim from prototype lines 6166-6173.
export const getSubscription = (state, packId) => {
    const sub = state.subscriptions?.[packId];
    if (sub) return sub;
    if (state.purchased?.includes(packId)) {
        return { status: 'active', startDate: null, expiryDate: addYearsIso(isoToday(), 1), price: null, synthetic: true };
    }
    return null;
};

// Returns 'active' | 'expiring' | 'expired' | 'none'.
//
// Migrated verbatim from prototype lines 6175-6183.
export const getSubStatus = (state, packId) => {
    const sub = getSubscription(state, packId);
    if (!sub) return 'none';
    const d = daysUntil(sub.expiryDate);
    if (d === null) return 'active';
    if (d < 0) return 'expired';
    if (d <= SUBSCRIPTION_WARN_DAYS) return 'expiring';
    return 'active';
};

// CLIENT-SIDE display/access helper only — NOT a production entitlement gate.
// Use this for UI display logic (e.g. lock badges, "expired" warnings).
// Production access enforcement = Cloud Function security rules (Phase 2/4).
//
// Migrated verbatim from prototype lines 6184-6187.
export const canAccessPack = (state, packId) => {
    const s = getSubStatus(state, packId);
    return s === 'active' || s === 'expiring';
};
